import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Camera, Hash, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const STEP_ICON = (m) => {
  if (m.inbound_text === "[foto]" || m.outcome === "photo_multi" || m.outcome === "photo_unreadable") return Camera;
  if (m.outcome === "win" || m.outcome === "lose") return Trophy;
  return Hash;
};

function Thread({ phone, msgs, outcomeStyle }) {
  const [open, setOpen] = useState(false);
  const ordered = useMemo(() => [...msgs].sort((a, b) => a.created_at.localeCompare(b.created_at)), [msgs]);
  const last = ordered[ordered.length - 1];
  const hasPhotoFlow = ordered.some((m) => m.outcome === "photo_multi");
  return (
    <li className="text-sm" data-testid={`whatsapp-thread-${phone}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-3 text-left hover:bg-secondary/40" data-testid={`whatsapp-thread-toggle-${phone}`}>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="font-semibold">{phone}</span>
        <span className="text-xs text-muted-foreground">{ordered.length} messaggi · ultimo {fmt(last.created_at)}</span>
        {hasPhotoFlow && <Badge className="border bg-sky-500/15 text-sky-300 border-sky-500/30">foto → scelta</Badge>}
        <Badge className={`ml-auto border ${outcomeStyle[last.outcome] || ""}`}>{last.outcome}</Badge>
      </button>
      {open && (
        <ol className="px-4 pb-3 space-y-1.5 border-l-2 border-border/60 ml-5" data-testid={`whatsapp-thread-steps-${phone}`}>
          {ordered.map((m) => {
            const Icon = STEP_ICON(m);
            return (
              <li key={m.id} className="pl-3 relative text-xs">
                <Icon className="h-3 w-3 absolute -left-[7px] top-0.5 bg-card text-muted-foreground" />
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="font-mono-code">{fmt(m.created_at)}</span>
                  <Badge className={`border ${outcomeStyle[m.outcome] || ""}`}>{m.outcome}</Badge>
                  {m.status === "failed" && <span className="text-red-400">invio fallito</span>}
                </div>
                {m.inbound_text && <p><span className="text-muted-foreground">Cliente:</span> <span className="font-mono-code">{m.inbound_text}</span></p>}
                {m.preview && <p className="rounded bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 whitespace-pre-wrap">{m.preview}</p>}
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}

export function ConversationThreads({ messages, outcomeStyle }) {
  const threads = useMemo(() => {
    const by = new Map();
    for (const m of messages) {
      if (!m.phone) continue;
      if (!by.has(m.phone)) by.set(m.phone, []);
      by.get(m.phone).push(m);
    }
    return [...by.entries()].sort((a, b) => b[1][0].created_at.localeCompare(a[1][0].created_at));
  }, [messages]);
  return (
    <ul className="divide-y divide-border max-h-[520px] overflow-auto" data-testid="whatsapp-threads">
      {threads.map(([phone, msgs]) => <Thread key={phone} phone={phone} msgs={msgs} outcomeStyle={outcomeStyle} />)}
    </ul>
  );
}
